// src/App.tsx
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './AuthContext';
import { Header } from './Header';
import { LandingPage } from './LandingPage';
import { Dashboard } from './Dashboard';
import { AdminAuthors } from './AdminAuthors';
import { Library } from './Library';
import { Projects } from './Projects';
import { ProjectPage } from './ProjectPage';
import { Jobs } from './Jobs';
import { AboutPage, TermsPage, PrivacyPage } from './StaticPages';
import { CookieBanner } from './CookieBanner';

/**
 * React Router's BrowserRouter swaps routes client-side without touching
 * scroll position — fine for in-place UI like the dashboard, but About/Terms/
 * Privacy are long pages linked from each other's footer, so clicking a
 * footer link (which requires scrolling to the bottom first) landed on the
 * next page still scrolled to that same position instead of its top. Keyed
 * on pathname only, not the full location, so it doesn't fight same-page
 * anchor jumps (Terms/Privacy's own #eligibility-style TOC links, which only
 * change the hash).
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppFooter() {
  return (
    <footer className="app-footer">
      <nav className="app-footer-links">
        <Link to="/about">About</Link>
        <Link to="/terms">Terms of Service</Link>
        <Link to="/privacy">Privacy Policy</Link>
      </nav>
    </footer>
  );
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="app-shell">
          <ScrollToTop />
          <Header />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/library" element={<Library />} />
            <Route path="/dashboard/projects" element={<Projects />} />
            <Route path="/dashboard/projects/:projectId" element={<ProjectPage />} />
            <Route path="/dashboard/jobs" element={<Jobs />} />
            <Route path="/dashboard/admin" element={<AdminAuthors />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <AppFooter />
        </div>
        <CookieBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}
